import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';

import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faArrowLeft, faCircleCheck, faCircleExclamation, faRotateLeft } from '@fortawesome/free-solid-svg-icons';
import { ControlValuesMatchValidator } from '../../core/validators/control-values-match.directive';
import { LoginService } from '../../core/services/login.service';
import { SessionStorageService } from '../../core/services/session-storage.service';

@Component({
  selector: 'app-reset-password',
  templateUrl: './reset-password.component.html',
  styleUrls: ['./reset-password.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    FontAwesomeModule,
    ReactiveFormsModule,
    RouterModule,
  ],
})
export class ResetPasswordComponent implements OnInit {
  private readonly loginService = inject(LoginService);
  private readonly route = inject(ActivatedRoute);
  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly sessionStorageService = inject(SessionStorageService);

  readonly showSaveControls = signal(true);
  readonly showNewPassword = signal(false);
  readonly showConfirmPassword = signal(false);
  readonly resetError = signal(false);
  readonly resetSuccess = signal(false);
  readonly validatingToken = signal(true);
  readonly error = signal('');

  token: string | null = '' ;
  readonly resetIcon = faRotateLeft ;
  readonly resetSuccessIcon = faCircleCheck ;
  readonly resetErrorIcon = faCircleExclamation ;
  readonly backIcon = faArrowLeft ;

  resetPasswordForm = this.fb.group({
    newPassword: ['', Validators.compose([
      Validators.required,
      Validators.minLength(8),
      Validators.maxLength(20),
      Validators.pattern('^(?=.*[0-9])(?=.*[a-zA-Z])([a-zA-Z0-9_!?]){8,20}$')
    ])],
    confirmPassword: [ '', Validators.required ]
  }, {
    validator: ControlValuesMatchValidator('newPassword', 'confirmPassword'),
  }) ;

  ngOnInit(): void {
    this.token = this.route.snapshot.paramMap.get('token') ;

    if (!this.token) {
      this.redirectToLoginWithError();
      return;
    }

    this.loginService.validateResetToken(this.token).subscribe({
      next: (status) => {
        if (status === 200) {
          this.validatingToken.set(false);
        } else {
          this.redirectToLoginWithError();
        }
      },
      error: () => {
        this.redirectToLoginWithError();
      }
    });
  }

  private redirectToLoginWithError(): void {
    this.sessionStorageService.setLogoutMessage(
      'The password reset request is invalid or has expired.', 'Invalid Password Reset Link'
    );
    this.router.navigateByUrl('/login');
  }

  changePassword() {
    const newPw = this.newPassword?.value?.trim() ?? '';
    const confirmPw = this.confirmPassword?.value?.trim() ?? '';

    if (newPw === '' || confirmPw === '' || newPw !== confirmPw)
    {
      // Sanity check, make sure our passwords match
      return ;
    }

    this.resetSuccess.set(false) ;
    this.resetError.set(false) ;
    this.error.set('') ;

    this.loginService.changePasswordViaToken(this.token!, newPw).subscribe({
      next: (status) => {
        if (status === 200)
        {
          this.resetError.set(false) ;
          this.resetSuccess.set(true) ;
          this.showSaveControls.set(false) ;
        }
        else
        {
          this.resetError.set(true) ;
          this.resetSuccess.set(false) ;

          if (status >= 500)
          {
            this.error.set('The application is temporarily unavailable. Please wait a few moments and try again.') ;
          }
          else if (status === 400)
          {
            this.error.set('The new password does not meet password requirements and is invalid.') ;
          }
          else if (status === 401)
          {
            this.error.set('The password reset request is invalid or has expired.')
          }
        }
      }
    }) ;
  }

  // Utility methods to get our form controls
  get newPassword() {
    return this.resetPasswordForm.get('newPassword')
  }

  get confirmPassword() {
    return this.resetPasswordForm.get('confirmPassword')
  }
}
